import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAppDispatch } from "../../common/state/hooks";
import { useLoading } from "../../common/components/AppState/IsLoading";
import { LoadingState } from "../../common/state/slice/appStateSlice";
import {
  fetchCurrentProjectGroupCompletion,
  getEnduserCurrentProjectGroupCompletion,
  getEnduserProjectGroupsCompletion,
} from "../../features/enduserStats/state/enduserStatsSlice";
import { useModulesCache } from "../../features/enduserStats/hooks";
import {
  SearchAndSort,
  SortOption,
  Pagination,
  EmptyState,
  SkeletonCard,
} from "../../features/statsNew/components";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import {
  useEnduserStatusOptions,
  type StatusFilter,
} from "../../features/enduserStats/components/enduserStatusOptions";
import EnduserModuleStatusCard from "../../features/enduserStats/components/EnduserModuleStatusCard";
import EnduserModuleFunnelChart from "../../features/enduserStats/components/EnduserModuleFunnelChart";
import EnduserDownloadsSection from "../../features/enduserStats/components/EnduserDownloadsSection";

const ENDUSER_PARCOURS_DETAIL_COMPONENT_ID = "enduserParcoursDetailView";

const EnduserParcoursDetailView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { parcoursId } = useParams<{ parcoursId: string }>();

  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);
  const currentProjectGroupCompletion = useSelector(getEnduserCurrentProjectGroupCompletion);
  const loadingState = useLoading(ENDUSER_PARCOURS_DETAIL_COMPONENT_ID);
  const isLoading = loadingState === LoadingState.Loading;

  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState("order-asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);

  // Find the parcours info
  const parcoursInfo = useMemo(() => {
    return projectGroupsCompletion.find((pg) => pg.projectGroupId === Number(parcoursId));
  }, [projectGroupsCompletion, parcoursId]);

  // Use buildId as dependency (primitive) instead of parcoursInfo (object) to avoid double fetch
  const buildId = parcoursInfo?.latestVersion?.buildId;
  const { shouldRefetchModules, markModulesAsFetched } = useModulesCache(buildId);

  // Fetch modules only if cache is invalid (buildId changed or visibility refresh)
  useEffect(() => {
    if (buildId && shouldRefetchModules) {
      dispatch(
        fetchCurrentProjectGroupCompletion({
          componentId: ENDUSER_PARCOURS_DETAIL_COMPONENT_ID,
          buildId,
        }),
      ).then(() => {
        markModulesAsFetched();
      });
    }
  }, [dispatch, buildId, shouldRefetchModules, markModulesAsFetched]);

  const sortOptions: SortOption[] = [
    { key: "order-asc", label: t("statsNew.sort.defaultOrder") },
    { key: "name-asc", label: t("statsNew.sort.nameAZ") },
    { key: "name-desc", label: t("statsNew.sort.nameZA") },
    { key: "activity-desc", label: t("statsNew.sort.activityRecent") },
  ];

  // Helper to determine module status from API fields
  const getModuleStatus = (module: (typeof currentProjectGroupCompletion)[0]) => {
    const isCompleted = module.progressPercentage === 100 || module.isPerfectScore === true;
    const hasStarted =
      !!module.dateLastSession ||
      (module.progressPercentage !== null && module.progressPercentage > 0);
    return { isCompleted, hasStarted };
  };

  // Filter and sort modules
  const filteredAndSortedData = useMemo(() => {
    let result = currentProjectGroupCompletion;

    // Filter by status using API fields
    if (statusFilter !== "all") {
      result = result.filter((module) => {
        const { isCompleted, hasStarted } = getModuleStatus(module);
        if (statusFilter === "completed") {
          return isCompleted;
        }
        if (statusFilter === "inProgress") {
          return hasStarted && !isCompleted;
        }
        if (statusFilter === "notStarted") {
          return !hasStarted;
        }
        return true;
      });
    }

    // Filter by search
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter((module) => module.projectName?.toLowerCase().includes(search));
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case "order-asc":
          return (a.order ?? 0) - (b.order ?? 0);
        case "name-asc":
          return (a.projectName ?? "").localeCompare(b.projectName ?? "");
        case "name-desc":
          return (b.projectName ?? "").localeCompare(a.projectName ?? "");
        case "activity-desc": {
          const aDate = a.sessions?.[0]?.createdAt
            ? new Date(a.sessions[0].createdAt).getTime()
            : 0;
          const bDate = b.sessions?.[0]?.createdAt
            ? new Date(b.sessions[0].createdAt).getTime()
            : 0;
          return bDate - aDate;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [currentProjectGroupCompletion, statusFilter, searchValue, sortKey]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, statusFilter, sortKey]);

  // Paginate the data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredAndSortedData.slice(start, start + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleModuleClick = useCallback(
    (moduleId: number) => {
      navigate(`/stats/parcours/${parcoursId}/${moduleId}`);
    },
    [navigate, parcoursId],
  );

  // Calculate status counts using API fields
  const statusCounts = useMemo(() => {
    const counts = { all: 0, completed: 0, inProgress: 0, notStarted: 0 };
    currentProjectGroupCompletion.forEach((module) => {
      counts.all++;
      const { isCompleted, hasStarted } = getModuleStatus(module);
      if (isCompleted) {
        counts.completed++;
      } else if (hasStarted) {
        counts.inProgress++;
      } else {
        counts.notStarted++;
      }
    });
    return counts;
  }, [currentProjectGroupCompletion]);

  const statusOptions = useEnduserStatusOptions(statusCounts, statusFilter);

  // Calculate overall progress using API fields
  const overallProgress = useMemo(() => {
    if (currentProjectGroupCompletion.length === 0) return 0;
    const completed = currentProjectGroupCompletion.filter((m) => {
      const { isCompleted } = getModuleStatus(m);
      return isCompleted;
    }).length;
    return Math.round((completed / currentProjectGroupCompletion.length) * 100);
  }, [currentProjectGroupCompletion]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse" />
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} variant="module" />
          ))}
        </div>
      </div>
    );
  }

  if (!parcoursInfo) {
    return (
      <EmptyState
        icon="folder"
        title={t("statsNew.parcours.notFound")}
        description={t("statsNew.emptyState.noParcoursDescription")}
      />
    );
  }

  const renderEmptyState = () => {
    if (searchValue) {
      return (
        <EmptyState
          icon="search"
          title={t("statsNew.emptyState.noSearchResults")}
          description={t("statsNew.emptyState.noSearchResultsDescription")}
        />
      );
    }

    // Status-specific empty states
    if (statusFilter === "notStarted") {
      return (
        <EmptyState
          icon="folder"
          title={t("enduserStats.module.noModulesNotStarted")}
          description={t("enduserStats.module.noModulesNotStartedDescription")}
        />
      );
    }

    if (statusFilter === "inProgress") {
      return (
        <EmptyState
          icon="folder"
          title={t("enduserStats.module.noModulesInProgress")}
          description={t("enduserStats.module.noModulesInProgressDescription")}
        />
      );
    }

    if (statusFilter === "completed") {
      return (
        <EmptyState
          icon="folder"
          title={t("enduserStats.module.noModulesCompleted")}
          description={t("enduserStats.module.noModulesCompletedDescription")}
        />
      );
    }

    return (
      <EmptyState
        icon="folder"
        title={t("enduserStats.module.noModules")}
        description={t("enduserStats.module.noModulesDescription")}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-2">
        <Link to="/stats/parcours">{t("enduserStats.tabs.parcours")}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <span className="stats-breadcrumb-current">{parcoursInfo.projectGroupName}</span>
      </nav>

      {/* Header with integrated module progression */}
      <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 px-4 py-3 mb-3 overflow-visible">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-gray-900">{parcoursInfo.projectGroupName}</h2>
              <p className="text-sm text-gray-500">
                {currentProjectGroupCompletion.length} {t("statsNew.parcours.modules")}
              </p>
            </div>
            {/* Module progression funnel with percentage at the end */}
            {currentProjectGroupCompletion.length > 0 && (
              <div className="flex items-center gap-4">
                <EnduserModuleFunnelChart modules={currentProjectGroupCompletion} />
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold text-green-600">{overallProgress}%</span>
                </div>
              </div>
            )}
          </div>
          {/* Downloads section */}
          <EnduserDownloadsSection
            projectGroupId={Number(parcoursId)}
            projectGroupName={parcoursInfo.projectGroupName}
          />
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex-shrink-0 mb-3">
        <SegmentedControl<StatusFilter>
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      {/* Search and Sort */}
      <div className="flex-shrink-0 mb-3">
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.module.searchPlaceholder")}
          sortOptions={sortOptions}
          selectedSort={sortKey}
          onSortChange={setSortKey}
        />
      </div>

      {/* Modules Grid */}
      {filteredAndSortedData.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="flex flex-wrap gap-4">
              {paginatedData.map((module) => (
                <EnduserModuleStatusCard
                  key={module.projectId}
                  module={module}
                  onClick={() => handleModuleClick(module.projectId)}
                />
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 mt-auto">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredAndSortedData.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EnduserParcoursDetailView;
